import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  TrendingUp, 
  Star, 
  ThumbDown, 
  Analytics,
  Assessment,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import DynamicPieChart from './DynamicPieChart';

const SectionContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '16px',
  marginBottom: '20px',
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: '340px 1fr',
    gap: '20px',
    marginBottom: '24px'
  }
}));

const ChartCard = styled(Box)(({ theme }) => ({
  background: 'white',
  borderRadius: '12px',
  padding: '16px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  border: '1px solid #e5e7eb',
  [theme.breakpoints.up('sm')]: {
    padding: '20px',
    borderRadius: '14px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '24px',
    borderRadius: '16px'
  }
}));

const InsightsCard = styled(Box)(({ theme }) => ({
  background: 'white',
  borderRadius: '12px',
  padding: '16px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  border: '1px solid #e5e7eb',
  [theme.breakpoints.up('sm')]: {
    padding: '20px',
    borderRadius: '14px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '24px',
    borderRadius: '16px'
  }
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '16px',
  [theme.breakpoints.up('md')]: {
    gap: '12px',
    marginBottom: '20px'
  }
}));

const TitleIcon = styled(Box)(({ theme, bgcolor }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  background: bgcolor || '#eff6ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    width: '36px',
    height: '36px',
    borderRadius: '10px'
  }
}));

const TitleText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#1e293b',
  [theme.breakpoints.up('md')]: {
    fontSize: '16px'
  }
}));

const InsightsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '12px',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px'
  }
}));

const InsightCard = styled(Box)(({ theme, accentcolor }) => ({
  background: '#f8fafc',
  borderRadius: '10px',
  padding: '14px',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.2s ease',
  WebkitTapHighlightColor: 'transparent',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '3px',
    background: accentcolor || '#3b82f6'
  },
  '&:active': {
    transform: 'scale(0.98)'
  },
  [theme.breakpoints.up('md')]: {
    borderRadius: '12px',
    padding: '16px',
    '&::before': {
      width: '4px'
    },
    '&:hover': {
      background: '#f1f5f9'
    },
    '&:active': {
      transform: 'scale(0.99)'
    }
  }
}));

const InsightTop = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  marginBottom: '10px',
  flexWrap: 'wrap',
  gap: '8px',
  [theme.breakpoints.up('md')]: {
    marginBottom: '12px'
  }
}));

const InsightLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  [theme.breakpoints.up('md')]: {
    gap: '8px'
  }
}));

const InsightIconBox = styled(Box)(({ theme, bgcolor }) => ({
  width: '28px',
  height: '28px',
  borderRadius: '6px',
  background: bgcolor || '#eff6ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    width: '32px',
    height: '32px',
    borderRadius: '8px'
  }
}));

const InsightName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#64748b',
  [theme.breakpoints.up('md')]: {
    fontSize: '13px'
  }
}));

const StatusBadge = styled(Box)(({ theme, performance }) => {
  const getColors = () => {
    switch (performance) {
      case 'excellent':
        return { bg: '#dcfce7', color: '#16a34a' };
      case 'good':
        return { bg: '#dbeafe', color: '#2563eb' };
      case 'average':
        return { bg: '#fef3c7', color: '#d97706' };
      case 'poor':
        return { bg: '#fee2e2', color: '#dc2626' };
      default:
        return { bg: '#f3f4f6', color: '#6b7280' };
    }
  };
  const colors = getColors();
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    padding: '3px 8px',
    borderRadius: '16px',
    background: colors.bg,
    color: colors.color,
    fontFamily: 'Inter, sans-serif',
    fontSize: '10px',
    fontWeight: 600,
    [theme.breakpoints.up('md')]: {
      gap: '4px',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '11px'
    }
  };
});

const InsightValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '22px',
  fontWeight: 700,
  color: '#1e293b',
  lineHeight: 1,
  [theme.breakpoints.up('md')]: {
    fontSize: '28px'
  }
}));

const InsightComparison = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  color: '#64748b',
  marginTop: '6px',
  [theme.breakpoints.up('md')]: {
    fontSize: '12px',
    marginTop: '8px'
  }
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  marginTop: '10px',
  [theme.breakpoints.up('md')]: {
    marginTop: '12px'
  }
}));

const ProgressBar = styled(Box)(({ theme }) => ({
  height: '5px',
  borderRadius: '2.5px',
  background: '#e2e8f0',
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    height: '6px',
    borderRadius: '3px'
  }
}));

const ProgressFill = styled(Box)(({ width, color }) => ({
  height: '100%',
  borderRadius: '3px',
  background: color || '#3b82f6',
  width: `${width}%`,
  transition: 'width 0.5s ease'
}));

const StatisticsSectionModern = ({ reputationData }) => {
  // Use real data if available, otherwise fallback to mock data
  const data = reputationData || {
    yourBusiness: { rating: 4.6, responseRate: 52, negativeRatio: 6, reviewCount: 20 },
    cityAverage: { rating: 4.2, responseRate: 36, negativeRatio: 23, reviewCount: 180 },
    topCompetitor: { rating: 4.4, responseRate: 48, negativeRatio: 44, reviewCount: 60 }
  };

  // Calculate insights
  const responseRatePerformance = data.yourBusiness.responseRate > data.cityAverage.responseRate ? 'good' : 'average';
  const ratingPerformance = data.yourBusiness.rating > data.cityAverage.rating ? 'excellent' : 'average';
  const negativeRatioPerformance = data.yourBusiness.negativeRatio < data.cityAverage.negativeRatio ? 'good' : 'poor';

  const getProgressColor = (performance) => {
    switch (performance) {
      case 'excellent': return '#16a34a';
      case 'good': return '#2563eb';
      case 'average': return '#d97706';
      case 'poor': return '#dc2626';
      default: return '#3b82f6';
    }
  };

  const insights = [
    {
      title: 'Response Rate',
      icon: TrendingUp,
      iconBg: '#ecfdf5',
      iconColor: '#10b981',
      accentColor: '#10b981',
      value: `${Math.round(data.yourBusiness.responseRate)}%`,
      comparison: data.yourBusiness.responseRate > data.cityAverage.responseRate ? 'above' : 'below',
      comparisonValue: `${Math.round(data.cityAverage.responseRate)}%`,
      performance: responseRatePerformance,
      progress: Math.min(100, data.yourBusiness.responseRate),
      comingSoon: true // Competitor data not available from DataForSEO
    },
    {
      title: 'Rating Score',
      icon: Star,
      iconBg: '#fef3c7',
      iconColor: '#f59e0b',
      accentColor: '#f59e0b',
      value: data.yourBusiness.rating.toFixed(1),
      comparison: data.yourBusiness.rating > data.cityAverage.rating ? 'above' : 'below',
      comparisonValue: data.cityAverage.rating.toFixed(1),
      performance: ratingPerformance,
      progress: (data.yourBusiness.rating / 5) * 100
    },
    {
      title: 'Negative Reviews',
      icon: ThumbDown,
      iconBg: '#fee2e2',
      iconColor: '#ef4444',
      accentColor: '#ef4444',
      value: `${Math.round(data.yourBusiness.negativeRatio)}%`,
      comparison: data.yourBusiness.negativeRatio < data.cityAverage.negativeRatio ? 'better' : 'worse',
      comparisonValue: `${Math.round(data.cityAverage.negativeRatio)}%`,
      performance: negativeRatioPerformance,
      progress: 100 - data.yourBusiness.negativeRatio,
      comingSoon: true // Competitor data not available from DataForSEO
    },
    {
      title: 'Review Volume',
      icon: Assessment,
      iconBg: '#ede9fe',
      iconColor: '#8b5cf6',
      accentColor: '#8b5cf6',
      value: `${data.yourBusiness.reviewCount}`,
      comparison: data.yourBusiness.reviewCount > data.cityAverage.reviewCount ? 'above' : 'below',
      comparisonValue: `${data.cityAverage.reviewCount}`,
      performance: data.yourBusiness.reviewCount > data.cityAverage.reviewCount ? 'good' : 'average',
      progress: Math.min(100, (data.yourBusiness.reviewCount / Math.max(data.cityAverage.reviewCount, 1)) * 100)
    }
  ];

  return (
    <SectionContainer>
      {/* Chart Section */}
      <ChartCard>
        <CardHeader>
          <TitleIcon bgcolor="#eff6ff">
            <Analytics sx={{ color: '#3b82f6', fontSize: '18px' }} />
          </TitleIcon>
          <TitleText>Rating Distribution</TitleText>
        </CardHeader>
        <DynamicPieChart reputationData={reputationData} />
      </ChartCard>

      {/* Insights Section */}
      <InsightsCard>
        <CardHeader>
          <TitleIcon bgcolor="#ecfdf5">
            <TrendingUp sx={{ color: '#10b981', fontSize: '18px' }} />
          </TitleIcon>
          <TitleText>Key Performance Insights</TitleText>
        </CardHeader>
        
        <InsightsGrid>
          {insights.map((insight, index) => (
            <InsightCard key={index} accentcolor={insight.accentColor}>
              <InsightTop>
                <InsightLabel>
                  <InsightIconBox bgcolor={insight.iconBg}>
                    <insight.icon sx={{ fontSize: '16px', color: insight.iconColor }} />
                  </InsightIconBox>
                  <InsightName>{insight.title}</InsightName>
                </InsightLabel>
                <StatusBadge performance={insight.performance}>
                  {insight.comparison === 'above' || insight.comparison === 'better' ? (
                    <ArrowUpward sx={{ fontSize: '12px' }} />
                  ) : (
                    <ArrowDownward sx={{ fontSize: '12px' }} />
                  )}
                  {insight.performance === 'excellent' ? 'Excellent' : 
                   insight.performance === 'good' ? 'Good' : 
                   insight.performance === 'average' ? 'Average' : 'Needs Work'}
                </StatusBadge>
              </InsightTop>
              
              <InsightValue>{insight.value}</InsightValue>
              {insight.comingSoon ? (
                <Box sx={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  borderRadius: '6px',
                  padding: '4px 12px',
                  marginTop: '4px'
                }}>
                  <Typography sx={{ 
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#6366F1',
                    whiteSpace: 'nowrap'
                  }}>
                    Coming Soon
                  </Typography>
                </Box>
              ) : (
                <InsightComparison>
                  vs {insight.comparisonValue} city avg
                </InsightComparison>
              )}
              
              <ProgressContainer>
                <ProgressBar>
                  <ProgressFill 
                    width={insight.progress} 
                    color={getProgressColor(insight.performance)} 
                  />
                </ProgressBar>
              </ProgressContainer>
            </InsightCard>
          ))}
        </InsightsGrid>
      </InsightsCard>
    </SectionContainer>
  );
};

export default StatisticsSectionModern;
